"use client";

import React, { useEffect, useRef, useState, useCallback } from "react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import Link from "next/link";

// ─── Types ───────────────────────────────────────────────────────────────────
interface TrustPoint {
  text: string;
}
interface Slide {
  id: number;
  badge: string;
  badgeColor: string;
  serviceName: string;
  headline: string;
  sub: string;
  cta1: { label: string; href: string };
  cta2: { label: string; href: string };
  trust: TrustPoint[];
  bgBloom: string;
  Animation: React.FC;
}

// ─── Slide Animations ────────────────────────────────────────────────────────

// SLIDE 1 — Managed IT
function ManagedITAnimation() {
  return (
    <svg viewBox="0 0 400 320" className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <radialGradient id="s1bg" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="rgba(37,99,235,0.15)" />
          <stop offset="100%" stopColor="transparent" />
        </radialGradient>
        <filter id="s1glow">
          <feGaussianBlur stdDeviation="3" result="blur" />
          <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
        </filter>
      </defs>
      <ellipse cx="200" cy="160" rx="180" ry="140" fill="url(#s1bg)" />

      {/* Central server rack */}
      <rect x="160" y="100" width="80" height="120" rx="6" fill="rgba(15,23,42,0.9)" stroke="rgba(59,130,246,0.5)" strokeWidth="1.5" />
      {[0,1,2,3,4].map(i => (
        <g key={i}>
          <rect x="168" y={110 + i * 22} width="64" height="14" rx="3" fill="rgba(30,41,59,0.8)" stroke="rgba(59,130,246,0.3)" strokeWidth="0.8" />
          <circle cx="178" cy={117 + i * 22} r="3" fill="#22C55E">
            <animate attributeName="opacity" values="1;0.3;1" dur={`${1.2 + i * 0.3}s`} repeatCount="indefinite" />
          </circle>
          <rect x="184" y={114 + i * 22} width="30" height="2" rx="1" fill="rgba(59,130,246,0.4)" />
          <rect x="184" y={118 + i * 22} width="20" height="2" rx="1" fill="rgba(34,211,238,0.3)" />
        </g>
      ))}

      {/* Connected endpoint nodes */}
      {[
        { cx: 60, cy: 80, label: "Laptop" },
        { cx: 340, cy: 80, label: "Desktop" },
        { cx: 60, cy: 240, label: "Mobile" },
        { cx: 340, cy: 240, label: "Server" },
      ].map((node, i) => (
        <g key={i}>
          <line x1={node.cx} y1={node.cy} x2="200" y2="160"
            stroke="rgba(59,130,246,0.2)" strokeWidth="1" strokeDasharray="4 3">
            <animate attributeName="stroke-dashoffset" values="0;-14" dur="2s" repeatCount="indefinite" />
          </line>
          <circle cx={node.cx} cy={node.cy} r="18" fill="rgba(15,23,42,0.9)" stroke="rgba(59,130,246,0.4)" strokeWidth="1.5" />
          <circle cx={node.cx} cy={node.cy} r="22" fill="none" stroke="rgba(59,130,246,0.15)" strokeWidth="1">
            <animate attributeName="r" values="18;26;18" dur={`${2 + i * 0.5}s`} repeatCount="indefinite" />
            <animate attributeName="opacity" values="0.6;0;0.6" dur={`${2 + i * 0.5}s`} repeatCount="indefinite" />
          </circle>
          <text x={node.cx} y={node.cy + 4} textAnchor="middle" fill="rgba(148,163,184,0.8)" fontSize="7" fontFamily="Inter">{node.label}</text>
        </g>
      ))}

      {/* Uptime graph */}
      <g transform="translate(120, 250)">
        <rect x="0" y="0" width="160" height="40" rx="4" fill="rgba(15,23,42,0.7)" stroke="rgba(59,130,246,0.2)" strokeWidth="1" />
        <polyline points="8,30 28,20 48,25 68,10 88,18 108,8 128,15 148,5"
          fill="none" stroke="#3B82F6" strokeWidth="1.5" strokeLinecap="round">
          <animate attributeName="stroke-dasharray" values="0,200;200,0" dur="3s" fill="freeze" />
        </polyline>
        <text x="8" y="38" fill="rgba(34,211,238,0.6)" fontSize="6" fontFamily="Inter">UPTIME 99.9%</text>
      </g>

      {/* Alert notification */}
      <g transform="translate(270, 50)">
        <rect x="0" y="0" width="90" height="28" rx="5" fill="rgba(34,197,94,0.15)" stroke="rgba(34,197,94,0.4)" strokeWidth="1">
          <animate attributeName="opacity" values="0;1;1;0" dur="4s" repeatCount="indefinite" begin="1s" />
        </rect>
        <circle cx="12" cy="14" r="4" fill="#22C55E">
          <animate attributeName="opacity" values="0;1;1;0" dur="4s" repeatCount="indefinite" begin="1s" />
        </circle>
        <text x="20" y="18" fill="rgba(34,197,94,0.9)" fontSize="7" fontFamily="Inter">
          <animate attributeName="opacity" values="0;1;1;0" dur="4s" repeatCount="indefinite" begin="1s" />
          All Systems OK
        </text>
      </g>
    </svg>
  );
}

// SLIDE 2 — Infrastructure
function InfrastructureAnimation() {
  const nodes2 = [
    { id: "HQ", cx: 200, cy: 160, r: 22, label: "HQ", primary: true },
    { id: "auk", cx: 80, cy: 80, r: 14, label: "Auckland" },
    { id: "wlg", cx: 320, cy: 70, r: 14, label: "Wellington" },
    { id: "chc", cx: 330, cy: 250, r: 14, label: "Christchurch" },
    { id: "ham", cx: 90, cy: 255, r: 14, label: "Hamilton" },
  ];
  const edges2 = [[0,1],[0,2],[0,3],[0,4]];
  return (
    <svg viewBox="0 0 400 320" className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <radialGradient id="s2bg" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="rgba(37,99,235,0.12)" />
          <stop offset="100%" stopColor="transparent" />
        </radialGradient>
      </defs>
      <ellipse cx="200" cy="160" rx="180" ry="140" fill="url(#s2bg)" />
      {edges2.map(([a, b], i) => {
        const na = nodes2[a], nb = nodes2[b];
        const len = Math.hypot(nb.cx - na.cx, nb.cy - na.cy);
        return (
          <g key={i}>
            <line x1={na.cx} y1={na.cy} x2={nb.cx} y2={nb.cy}
              stroke="rgba(59,130,246,0.25)" strokeWidth="1.5" strokeDasharray="5 3" />
            {/* travelling light */}
            <circle r="3" fill="#60A5FA" opacity="0.9">
              <animateMotion dur={`${2 + i * 0.4}s`} repeatCount="indefinite" begin={`${i * 0.5}s`}>
                <mpath href={`#path2-${i}`} />
              </animateMotion>
              <animate attributeName="opacity" values="0;1;1;0" dur={`${2 + i * 0.4}s`} repeatCount="indefinite" begin={`${i * 0.5}s`} />
            </circle>
            <path id={`path2-${i}`} d={`M${na.cx},${na.cy} L${nb.cx},${nb.cy}`} fill="none" />
          </g>
        );
      })}
      {nodes2.map((n, i) => (
        <g key={n.id}>
          <circle cx={n.cx} cy={n.cy} r={n.r + 8} fill="none" stroke={n.primary ? "rgba(59,130,246,0.3)" : "rgba(59,130,246,0.15)"} strokeWidth="1">
            <animate attributeName="r" values={`${n.r + 4};${n.r + 14};${n.r + 4}`} dur={`${2.5 + i * 0.3}s`} repeatCount="indefinite" />
            <animate attributeName="opacity" values="0.5;0;0.5" dur={`${2.5 + i * 0.3}s`} repeatCount="indefinite" />
          </circle>
          <circle cx={n.cx} cy={n.cy} r={n.r} fill={n.primary ? "rgba(37,99,235,0.8)" : "rgba(30,58,138,0.7)"} stroke={n.primary ? "#3B82F6" : "rgba(96,165,250,0.5)"} strokeWidth="1.5" />
          <text x={n.cx} y={n.cy + 4} textAnchor="middle" fill="white" fontSize={n.primary ? "8" : "6"} fontFamily="Inter" fontWeight="600">{n.label}</text>
        </g>
      ))}
    </svg>
  );
}

// SLIDE 3 — Network
function NetworkAnimation() {
  return (
    <svg viewBox="0 0 400 320" className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <radialGradient id="s3bg" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="rgba(37,99,235,0.12)" />
          <stop offset="100%" stopColor="transparent" />
        </radialGradient>
      </defs>
      <ellipse cx="200" cy="160" rx="180" ry="140" fill="url(#s3bg)" />

      {/* Router at top */}
      <rect x="175" y="30" width="50" height="35" rx="5" fill="rgba(15,23,42,0.9)" stroke="rgba(59,130,246,0.6)" strokeWidth="1.5" />
      <text x="200" y="52" textAnchor="middle" fill="#60A5FA" fontSize="8" fontFamily="Inter">ROUTER</text>

      {/* Firewall */}
      <rect x="175" y="110" width="50" height="35" rx="5" fill="rgba(15,23,42,0.9)" stroke="rgba(239,68,68,0.5)" strokeWidth="1.5" />
      <text x="200" y="132" textAnchor="middle" fill="#F87171" fontSize="8" fontFamily="Inter">FIREWALL</text>
      {/* Shield pulse on firewall */}
      <circle cx="200" cy="127" r="22" fill="none" stroke="rgba(239,68,68,0.2)" strokeWidth="1">
        <animate attributeName="r" values="22;32;22" dur="2s" repeatCount="indefinite" />
        <animate attributeName="opacity" values="0.5;0;0.5" dur="2s" repeatCount="indefinite" />
      </circle>

      {/* Switch */}
      <rect x="175" y="190" width="50" height="30" rx="5" fill="rgba(15,23,42,0.9)" stroke="rgba(34,211,238,0.5)" strokeWidth="1.5" />
      <text x="200" y="209" textAnchor="middle" fill="#22D3EE" fontSize="8" fontFamily="Inter">SWITCH</text>

      {/* Connections */}
      <line x1="200" y1="65" x2="200" y2="110" stroke="rgba(59,130,246,0.4)" strokeWidth="1.5" strokeDasharray="4 3">
        <animate attributeName="stroke-dashoffset" values="0;-14" dur="1.5s" repeatCount="indefinite" />
      </line>
      <line x1="200" y1="145" x2="200" y2="190" stroke="rgba(59,130,246,0.4)" strokeWidth="1.5" strokeDasharray="4 3">
        <animate attributeName="stroke-dashoffset" values="0;-14" dur="1.5s" repeatCount="indefinite" />
      </line>

      {/* Access points with signal waves */}
      {[
        { cx: 80, cy: 240, label: "AP-1" },
        { cx: 200, cy: 270, label: "AP-2" },
        { cx: 320, cy: 240, label: "AP-3" },
      ].map((ap, i) => (
        <g key={i}>
          <line x1={ap.cx} y1={ap.cy} x2="200" y2="220" stroke="rgba(34,211,238,0.3)" strokeWidth="1" strokeDasharray="3 3">
            <animate attributeName="stroke-dashoffset" values="0;-12" dur="2s" repeatCount="indefinite" begin={`${i * 0.3}s`} />
          </line>
          <circle cx={ap.cx} cy={ap.cy} r="12" fill="rgba(15,23,42,0.9)" stroke="rgba(34,211,238,0.5)" strokeWidth="1.5" />
          <text x={ap.cx} y={ap.cy + 4} textAnchor="middle" fill="#22D3EE" fontSize="6" fontFamily="Inter">{ap.label}</text>
          {/* Signal waves */}
          {[1,2,3].map(w => (
            <path key={w} d={`M${ap.cx - w*8},${ap.cy - 8} Q${ap.cx},${ap.cy - 8 - w*6} ${ap.cx + w*8},${ap.cy - 8}`}
              fill="none" stroke="rgba(34,211,238,0.4)" strokeWidth="1">
              <animate attributeName="opacity" values="0;0.8;0" dur="2s" repeatCount="indefinite" begin={`${i * 0.3 + w * 0.2}s`} />
            </path>
          ))}
        </g>
      ))}

      {/* Data packets */}
      {[0,1,2].map(i => (
        <circle key={i} r="3" fill="#3B82F6" opacity="0.8">
          <animateMotion dur={`${1.5 + i * 0.3}s`} repeatCount="indefinite" begin={`${i * 0.5}s`}>
            <mpath href={`#netpath-${i}`} />
          </animateMotion>
          <animate attributeName="opacity" values="0;1;1;0" dur={`${1.5 + i * 0.3}s`} repeatCount="indefinite" begin={`${i * 0.5}s`} />
        </circle>
      ))}
      <path id="netpath-0" d="M200,65 L200,110" fill="none" />
      <path id="netpath-1" d="M200,145 L200,190" fill="none" />
      <path id="netpath-2" d="M200,220 L80,240" fill="none" />
    </svg>
  );
}

// SLIDE 4 — Cybersecurity
function CybersecurityAnimation() {
  return (
    <svg viewBox="0 0 400 320" className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <radialGradient id="s4bg" cx="50%" cy="50%" r="60%">
          <stop offset="0%" stopColor="rgba(239,68,68,0.08)" />
          <stop offset="50%" stopColor="rgba(37,99,235,0.08)" />
          <stop offset="100%" stopColor="transparent" />
        </radialGradient>
      </defs>
      <ellipse cx="200" cy="160" rx="180" ry="140" fill="url(#s4bg)" />

      {/* Central shield */}
      <path d="M200,60 L240,80 L240,140 Q240,180 200,200 Q160,180 160,140 L160,80 Z"
        fill="rgba(37,99,235,0.2)" stroke="#3B82F6" strokeWidth="2">
        <animate attributeName="stroke" values="#3B82F6;#EF4444;#3B82F6" dur="4s" repeatCount="indefinite" />
      </path>
      <path d="M200,60 L240,80 L240,140 Q240,180 200,200 Q160,180 160,140 L160,80 Z"
        fill="none" stroke="rgba(59,130,246,0.3)" strokeWidth="1">
        <animate attributeName="d" values="M200,60 L240,80 L240,140 Q240,180 200,200 Q160,180 160,140 L160,80 Z;M200,55 L245,78 L245,142 Q245,185 200,205 Q155,185 155,142 L155,78 Z;M200,60 L240,80 L240,140 Q240,180 200,200 Q160,180 160,140 L160,80 Z" dur="3s" repeatCount="indefinite" />
      </path>
      {/* Shield check */}
      <path d="M188,128 L196,138 L214,118" fill="none" stroke="#60A5FA" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />

      {/* Orbit rings */}
      {[50, 75, 100].map((r, i) => (
        <ellipse key={i} cx="200" cy="130" rx={r} ry={r * 0.4}
          fill="none" stroke="rgba(59,130,246,0.15)" strokeWidth="1" strokeDasharray="4 3" />
      ))}

      {/* Orbiting security icons */}
      {[
        { label: "🔒", dur: "6s", rx: 50, ry: 20 },
        { label: "👁", dur: "8s", rx: 75, ry: 30, begin: "2s" },
        { label: "⚠", dur: "10s", rx: 100, ry: 40, begin: "4s" },
        { label: "✓", dur: "7s", rx: 62, ry: 25, begin: "1s" },
      ].map((icon, i) => (
        <text key={i} fontSize="12" textAnchor="middle">
          <animateMotion dur={icon.dur} repeatCount="indefinite" begin={icon.begin || "0s"}>
            <mpath href={`#orbit-${i}`} />
          </animateMotion>
          {icon.label}
        </text>
      ))}
      <path id="orbit-0" d="M250,130 A50,20 0 1,1 249.9,130" fill="none" />
      <path id="orbit-1" d="M275,130 A75,30 0 1,1 274.9,130" fill="none" />
      <path id="orbit-2" d="M300,130 A100,40 0 1,1 299.9,130" fill="none" />
      <path id="orbit-3" d="M262,130 A62,25 0 1,1 261.9,130" fill="none" />

      {/* Scanning line */}
      <line x1="100" y1="130" x2="300" y2="130" stroke="rgba(34,211,238,0.5)" strokeWidth="1.5">
        <animate attributeName="y1" values="80;200;80" dur="3s" repeatCount="indefinite" />
        <animate attributeName="y2" values="80;200;80" dur="3s" repeatCount="indefinite" />
        <animate attributeName="opacity" values="0;0.8;0" dur="3s" repeatCount="indefinite" />
      </line>

      {/* Threat nodes being neutralised */}
      {[
        { x: 50, y: 100 }, { x: 350, y: 90 }, { x: 60, y: 220 }, { x: 340, y: 210 }
      ].map((t, i) => (
        <g key={i}>
          <circle cx={t.x} cy={t.y} r="8" fill="rgba(239,68,68,0.3)" stroke="#EF4444" strokeWidth="1">
            <animate attributeName="fill" values="rgba(239,68,68,0.3);rgba(34,197,94,0.3);rgba(239,68,68,0.3)" dur={`${3 + i}s`} repeatCount="indefinite" begin={`${i * 0.7}s`} />
            <animate attributeName="stroke" values="#EF4444;#22C55E;#EF4444" dur={`${3 + i}s`} repeatCount="indefinite" begin={`${i * 0.7}s`} />
          </circle>
          <text x={t.x} y={t.y + 4} textAnchor="middle" fill="rgba(239,68,68,0.8)" fontSize="8">
            ✕
            <animate attributeName="fill" values="rgba(239,68,68,0.8);rgba(34,197,94,0.8);rgba(239,68,68,0.8)" dur={`${3 + i}s`} repeatCount="indefinite" begin={`${i * 0.7}s`} />
          </text>
        </g>
      ))}
    </svg>
  );
}

// SLIDE 5 — Cloud & M365
function CloudAnimation() {
  const apps = [
    { label: "Teams", color: "#5B5EA6", angle: 0 },
    { label: "Outlook", color: "#0078D4", angle: 60 },
    { label: "SharePoint", color: "#038387", angle: 120 },
    { label: "OneDrive", color: "#0078D4", angle: 180 },
    { label: "Azure", color: "#0089D6", angle: 240 },
    { label: "Intune", color: "#0078D4", angle: 300 },
  ];
  return (
    <svg viewBox="0 0 400 320" className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <radialGradient id="s5bg" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="rgba(14,165,233,0.18)" />
          <stop offset="100%" stopColor="transparent" />
        </radialGradient>
        <radialGradient id="s5cloudGrad" cx="50%" cy="60%" r="60%">
          <stop offset="0%" stopColor="rgba(56,189,248,0.5)" />
          <stop offset="100%" stopColor="rgba(14,165,233,0.15)" />
        </radialGradient>
        <filter id="s5glow">
          <feGaussianBlur stdDeviation="3" result="blur" />
          <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
        </filter>
      </defs>

      {/* Background bloom */}
      <ellipse cx="200" cy="155" rx="185" ry="145" fill="url(#s5bg)" />

      {/* Outer pulse rings */}
      {[70, 95, 120].map((r, i) => (
        <circle key={i} cx="200" cy="140" r={r} fill="none" stroke="rgba(14,165,233,0.15)" strokeWidth="1" strokeDasharray="5 4">
          <animate attributeName="r" values={`${r};${r + 8};${r}`} dur={`${3 + i * 0.6}s`} repeatCount="indefinite" begin={`${i * 0.4}s`} />
          <animate attributeName="opacity" values="0.5;0.1;0.5" dur={`${3 + i * 0.6}s`} repeatCount="indefinite" begin={`${i * 0.4}s`} />
        </circle>
      ))}

      {/* Central cloud shape — layered for depth */}
      {/* Shadow layer */}
      <path d="M155,152 Q148,130 162,120 Q165,103 182,100 Q190,90 200,90 Q213,90 220,100 Q237,98 244,114 Q258,116 258,132 Q265,148 255,155 Z"
        fill="rgba(14,165,233,0.08)" transform="translate(4,6)" />
      {/* Main cloud */}
      <path d="M155,152 Q148,130 162,120 Q165,103 182,100 Q190,90 200,90 Q213,90 220,100 Q237,98 244,114 Q258,116 258,132 Q265,148 255,155 Z"
        fill="url(#s5cloudGrad)" stroke="#38BDF8" strokeWidth="1.8">
        <animate attributeName="stroke-opacity" values="0.7;1;0.7" dur="3s" repeatCount="indefinite" />
      </path>
      {/* Cloud highlight */}
      <path d="M175,118 Q178,108 190,106 Q200,102 210,108"
        fill="none" stroke="rgba(255,255,255,0.35)" strokeWidth="2" strokeLinecap="round" />
      {/* Cloud label */}
      <text x="205" y="132" textAnchor="middle" fill="white" fontSize="9" fontFamily="Inter" fontWeight="700" letterSpacing="0.5">CLOUD</text>
      {/* Glow under cloud */}
      <ellipse cx="205" cy="158" rx="52" ry="6" fill="rgba(14,165,233,0.25)">
        <animate attributeName="rx" values="52;60;52" dur="3s" repeatCount="indefinite" />
        <animate attributeName="opacity" values="0.4;0.7;0.4" dur="3s" repeatCount="indefinite" />
      </ellipse>

      {/* Spokes + app nodes */}
      {apps.map((app, i) => {
        const rad = (app.angle * Math.PI) / 180;
        const orbitRx = 115;
        const orbitRy = 42;
        const x = 200 + orbitRx * Math.cos(rad);
        const y = 140 + orbitRy * Math.sin(rad);
        return (
          <g key={i}>
            {/* Spoke line */}
            <line x1={x} y1={y} x2="200" y2="140"
              stroke="rgba(14,165,233,0.2)" strokeWidth="1" strokeDasharray="4 3">
              <animate attributeName="stroke-dashoffset" values="0;-14" dur="2.5s" repeatCount="indefinite" begin={`${i * 0.4}s`} />
            </line>
            {/* Travelling data packet along spoke */}
            <circle r="2.5" fill="#38BDF8" opacity="0.9">
              <animateMotion dur={`${1.6 + i * 0.2}s`} repeatCount="indefinite" begin={`${i * 0.35}s`}>
                <mpath href={`#cloudpath-${i}`} />
              </animateMotion>
              <animate attributeName="opacity" values="0;1;1;0" dur={`${1.6 + i * 0.2}s`} repeatCount="indefinite" begin={`${i * 0.35}s`} />
            </circle>
            <path id={`cloudpath-${i}`} d={`M200,140 L${x},${y}`} fill="none" />
            {/* App node circle */}
            <circle cx={x} cy={y} r="18" fill="rgba(15,23,42,0.92)" stroke={app.color} strokeWidth="1.8">
              <animate attributeName="stroke-opacity" values="0.7;1;0.7" dur={`${2 + i * 0.3}s`} repeatCount="indefinite" begin={`${i * 0.2}s`} />
            </circle>
            {/* Pulse ring on node */}
            <circle cx={x} cy={y} r="22" fill="none" stroke={app.color} strokeWidth="1" opacity="0.3">
              <animate attributeName="r" values="18;28;18" dur={`${2.5 + i * 0.3}s`} repeatCount="indefinite" begin={`${i * 0.25}s`} />
              <animate attributeName="opacity" values="0.5;0;0.5" dur={`${2.5 + i * 0.3}s`} repeatCount="indefinite" begin={`${i * 0.25}s`} />
            </circle>
            {/* App label — static, always readable */}
            <text x={x} y={y + 3} textAnchor="middle" fill="white" fontSize="5.5" fontFamily="Inter" fontWeight="600">{app.label}</text>
          </g>
        );
      })}

      {/* Endpoint devices at bottom */}
      {[
        { cx: 90, cy: 268, label: "PC" },
        { cx: 200, cy: 282, label: "Mobile" },
        { cx: 310, cy: 268, label: "Laptop" },
      ].map((d, i) => (
        <g key={i}>
          <line x1={d.cx} y1={d.cy - 12} x2="200" y2="158"
            stroke="rgba(14,165,233,0.2)" strokeWidth="1" strokeDasharray="3 3">
            <animate attributeName="stroke-dashoffset" values="0;-12" dur="2.5s" repeatCount="indefinite" begin={`${i * 0.4}s`} />
          </line>
          <rect x={d.cx - 18} y={d.cy - 14} width="36" height="26" rx="5"
            fill="rgba(15,23,42,0.9)" stroke="rgba(14,165,233,0.45)" strokeWidth="1.5">
            <animate attributeName="stroke-opacity" values="0.45;0.9;0.45" dur={`${2 + i * 0.4}s`} repeatCount="indefinite" begin={`${i * 0.5}s`} />
          </rect>
          <text x={d.cx} y={d.cy + 4} textAnchor="middle" fill="rgba(148,163,184,0.85)" fontSize="6" fontFamily="Inter">{d.label}</text>
          {/* Sync arrow */}
          <path d={`M${d.cx - 7},${d.cy - 20} L${d.cx},${d.cy - 25} L${d.cx + 7},${d.cy - 20}`}
            fill="none" stroke="rgba(14,165,233,0.7)" strokeWidth="1.5" strokeLinecap="round">
            <animate attributeName="opacity" values="0;1;0" dur="2.2s" repeatCount="indefinite" begin={`${i * 0.6}s`} />
          </path>
        </g>
      ))}

      {/* Status badge */}
      <g transform="translate(268, 48)">
        <rect x="0" y="0" width="96" height="28" rx="5"
          fill="rgba(14,165,233,0.15)" stroke="rgba(14,165,233,0.5)" strokeWidth="1">
          <animate attributeName="opacity" values="0;1;1;0" dur="4s" repeatCount="indefinite" begin="1.5s" />
        </rect>
        <circle cx="12" cy="14" r="4" fill="#38BDF8">
          <animate attributeName="opacity" values="0;1;1;0" dur="4s" repeatCount="indefinite" begin="1.5s" />
        </circle>
        <text x="22" y="18" fill="rgba(56,189,248,0.95)" fontSize="7" fontFamily="Inter" fontWeight="600">
          <animate attributeName="opacity" values="0;1;1;0" dur="4s" repeatCount="indefinite" begin="1.5s" />
          Synced ✓
        </text>
      </g>
    </svg>
  );
}

// SLIDE 6 — Business Communications
function CommsAnimation() {
  return (
    <svg viewBox="0 0 400 320" className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <radialGradient id="s6bg" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="rgba(6,182,212,0.12)" />
          <stop offset="100%" stopColor="transparent" />
        </radialGradient>
      </defs>
      <ellipse cx="200" cy="160" rx="180" ry="140" fill="url(#s6bg)" />

      {/* Central headset */}
      <circle cx="200" cy="140" r="30" fill="rgba(15,23,42,0.9)" stroke="rgba(6,182,212,0.6)" strokeWidth="2" />
      <path d="M185,130 Q185,115 200,115 Q215,115 215,130" fill="none" stroke="#06B6D4" strokeWidth="2.5" strokeLinecap="round" />
      <rect x="182" y="128" width="8" height="14" rx="4" fill="#06B6D4" />
      <rect x="210" y="128" width="8" height="14" rx="4" fill="#06B6D4" />

      {/* Sound waves */}
      {[1,2,3,4].map(w => (
        <circle key={w} cx="200" cy="140" r={30 + w * 18} fill="none" stroke="rgba(6,182,212,0.3)" strokeWidth="1">
          <animate attributeName="r" values={`${30 + w*18};${30 + w*18 + 10};${30 + w*18}`} dur={`${2 + w * 0.3}s`} repeatCount="indefinite" />
          <animate attributeName="opacity" values="0.5;0;0.5" dur={`${2 + w * 0.3}s`} repeatCount="indefinite" />
        </circle>
      ))}

      {/* Location bubbles */}
      {[
        { cx: 60, cy: 80, label: "Office" },
        { cx: 340, cy: 80, label: "Remote" },
        { cx: 60, cy: 240, label: "Mobile" },
        { cx: 340, cy: 240, label: "Branch" },
      ].map((loc, i) => (
        <g key={i}>
          <line x1={loc.cx} y1={loc.cy} x2="200" y2="140"
            stroke="rgba(6,182,212,0.25)" strokeWidth="1.5" strokeDasharray="4 3">
            <animate attributeName="stroke-dashoffset" values="0;-14" dur={`${1.5 + i * 0.2}s`} repeatCount="indefinite" />
          </line>
          <rect x={loc.cx - 24} y={loc.cy - 14} width="48" height="28" rx="6"
            fill="rgba(15,23,42,0.9)" stroke="rgba(6,182,212,0.4)" strokeWidth="1.5" />
          <text x={loc.cx} y={loc.cy + 4} textAnchor="middle" fill="#22D3EE" fontSize="7" fontFamily="Inter" fontWeight="600">{loc.label}</text>
          {/* Call connecting animation */}
          <circle cx={loc.cx} cy={loc.cy} r="18" fill="none" stroke="rgba(6,182,212,0.3)" strokeWidth="1">
            <animate attributeName="r" values="18;28;18" dur={`${2 + i * 0.4}s`} repeatCount="indefinite" begin={`${i * 0.5}s`} />
            <animate attributeName="opacity" values="0.6;0;0.6" dur={`${2 + i * 0.4}s`} repeatCount="indefinite" begin={`${i * 0.5}s`} />
          </circle>
        </g>
      ))}
    </svg>
  );
}

// SLIDE 7 — AI & Automation
function AIAnimation() {
  const neuralNodes = [
    // Input layer
    { cx: 60, cy: 80 }, { cx: 60, cy: 140 }, { cx: 60, cy: 200 }, { cx: 60, cy: 260 },
    // Hidden layer 1
    { cx: 160, cy: 100 }, { cx: 160, cy: 160 }, { cx: 160, cy: 220 },
    // Hidden layer 2
    { cx: 250, cy: 120 }, { cx: 250, cy: 180 }, { cx: 250, cy: 240 },
    // Output layer
    { cx: 340, cy: 140 }, { cx: 340, cy: 200 },
  ];
  const connections = [
    [0,4],[0,5],[1,4],[1,5],[1,6],[2,5],[2,6],[3,6],
    [4,7],[4,8],[5,7],[5,8],[5,9],[6,8],[6,9],
    [7,10],[7,11],[8,10],[8,11],[9,11],
  ];
  return (
    <svg viewBox="0 0 400 320" className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <radialGradient id="s7bg" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="rgba(124,58,237,0.15)" />
          <stop offset="50%" stopColor="rgba(6,182,212,0.08)" />
          <stop offset="100%" stopColor="transparent" />
        </radialGradient>
        <linearGradient id="s7line" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#7C3AED" stopOpacity="0.4" />
          <stop offset="100%" stopColor="#06B6D4" stopOpacity="0.4" />
        </linearGradient>
      </defs>
      <ellipse cx="200" cy="170" rx="190" ry="150" fill="url(#s7bg)" />

      {/* Connections */}
      {connections.map(([a, b], i) => (
        <line key={i}
          x1={neuralNodes[a].cx} y1={neuralNodes[a].cy}
          x2={neuralNodes[b].cx} y2={neuralNodes[b].cy}
          stroke="url(#s7line)" strokeWidth="1">
          <animate attributeName="opacity" values="0.2;0.8;0.2" dur={`${1.5 + (i % 5) * 0.3}s`} repeatCount="indefinite" begin={`${(i % 7) * 0.2}s`} />
        </line>
      ))}

      {/* Data flowing along connections */}
      {connections.slice(0, 8).map(([a, b], i) => (
        <circle key={i} r="3" fill="#7C3AED" opacity="0.9">
          <animateMotion dur={`${1 + i * 0.15}s`} repeatCount="indefinite" begin={`${i * 0.3}s`}>
            <mpath href={`#aipath-${i}`} />
          </animateMotion>
          <animate attributeName="fill" values="#7C3AED;#06B6D4;#7C3AED" dur={`${1 + i * 0.15}s`} repeatCount="indefinite" begin={`${i * 0.3}s`} />
          <animate attributeName="opacity" values="0;1;1;0" dur={`${1 + i * 0.15}s`} repeatCount="indefinite" begin={`${i * 0.3}s`} />
        </circle>
      ))}
      {connections.slice(0, 8).map(([a, b], i) => (
        <path key={i} id={`aipath-${i}`} d={`M${neuralNodes[a].cx},${neuralNodes[a].cy} L${neuralNodes[b].cx},${neuralNodes[b].cy}`} fill="none" />
      ))}

      {/* Nodes */}
      {neuralNodes.map((n, i) => (
        <g key={i}>
          <circle cx={n.cx} cy={n.cy} r="10" fill="rgba(15,23,42,0.9)" stroke={i >= 10 ? "#06B6D4" : i >= 7 ? "#7C3AED" : "rgba(124,58,237,0.6)"} strokeWidth="1.5">
            <animate attributeName="stroke-opacity" values="0.6;1;0.6" dur={`${1.5 + i * 0.2}s`} repeatCount="indefinite" begin={`${i * 0.1}s`} />
          </circle>
          <circle cx={n.cx} cy={n.cy} r="4" fill={i >= 10 ? "#06B6D4" : "#7C3AED"} opacity="0.8">
            <animate attributeName="r" values="3;5;3" dur={`${1.5 + i * 0.2}s`} repeatCount="indefinite" begin={`${i * 0.1}s`} />
          </circle>
        </g>
      ))}

      {/* Process nodes lighting up */}
      {[
        { cx: 130, cy: 290, label: "Input" },
        { cx: 200, cy: 290, label: "Process" },
        { cx: 270, cy: 290, label: "Output" },
      ].map((p, i) => (
        <g key={i}>
          <rect x={p.cx - 28} y={p.cy - 10} width="56" height="20" rx="4"
            fill="rgba(124,58,237,0.2)" stroke="rgba(124,58,237,0.5)" strokeWidth="1">
            <animate attributeName="fill" values="rgba(124,58,237,0.2);rgba(6,182,212,0.2);rgba(124,58,237,0.2)" dur="3s" repeatCount="indefinite" begin={`${i * 1}s`} />
          </rect>
          <text x={p.cx} y={p.cy + 4} textAnchor="middle" fill="rgba(167,139,250,0.9)" fontSize="6" fontFamily="Inter">{p.label}</text>
        </g>
      ))}
    </svg>
  );
}

// SLIDE 8 — ICT Procurement
function ProcurementAnimation() {
  return (
    <svg viewBox="0 0 400 320" className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <radialGradient id="s8bg" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="rgba(37,99,235,0.12)" />
          <stop offset="100%" stopColor="transparent" />
        </radialGradient>
      </defs>
      <ellipse cx="200" cy="160" rx="180" ry="140" fill="url(#s8bg)" />

      {/* Laptop */}
      <g transform="translate(60, 80)">
        <rect x="0" y="20" width="80" height="50" rx="4" fill="rgba(15,23,42,0.9)" stroke="rgba(59,130,246,0.6)" strokeWidth="1.5">
          <animate attributeName="stroke-opacity" values="0.6;1;0.6" dur="2s" repeatCount="indefinite" />
        </rect>
        <rect x="-5" y="70" width="90" height="6" rx="2" fill="rgba(30,58,138,0.9)" stroke="rgba(59,130,246,0.5)" strokeWidth="1" />
        <rect x="5" y="25" width="70" height="40" rx="2" fill="rgba(30,58,138,0.5)" />
        <text x="40" y="50" textAnchor="middle" fill="rgba(96,165,250,0.8)" fontSize="8" fontFamily="Inter">LAPTOP</text>
        {/* Glow edge */}
        <rect x="0" y="20" width="80" height="50" rx="4" fill="none" stroke="rgba(59,130,246,0.3)" strokeWidth="4" filter="url(#s8glow)" />
      </g>

      {/* Server rack */}
      <g transform="translate(160, 60)">
        <rect x="0" y="0" width="60" height="100" rx="4" fill="rgba(15,23,42,0.9)" stroke="rgba(59,130,246,0.6)" strokeWidth="1.5">
          <animate attributeName="stroke-opacity" values="0.6;1;0.6" dur="2.5s" repeatCount="indefinite" begin="0.5s" />
        </rect>
        {[0,1,2,3].map(i => (
          <rect key={i} x="5" y={8 + i * 22} width="50" height="14" rx="2" fill="rgba(30,41,59,0.8)" stroke="rgba(59,130,246,0.3)" strokeWidth="0.8" />
        ))}
        <text x="30" y="108" textAnchor="middle" fill="rgba(96,165,250,0.7)" fontSize="7" fontFamily="Inter">SERVER</text>
      </g>

      {/* Network switch */}
      <g transform="translate(270, 100)">
        <rect x="0" y="0" width="80" height="35" rx="4" fill="rgba(15,23,42,0.9)" stroke="rgba(34,211,238,0.5)" strokeWidth="1.5">
          <animate attributeName="stroke-opacity" values="0.5;1;0.5" dur="3s" repeatCount="indefinite" begin="1s" />
        </rect>
        {[0,1,2,3,4,5,6,7].map(i => (
          <rect key={i} x={5 + i * 9} y="8" width="6" height="18" rx="1" fill="rgba(30,41,59,0.8)" stroke="rgba(34,211,238,0.3)" strokeWidth="0.5" />
        ))}
        <text x="40" y="44" textAnchor="middle" fill="rgba(34,211,238,0.7)" fontSize="7" fontFamily="Inter">SWITCH</text>
      </g>

      {/* Mobile device */}
      <g transform="translate(300, 170)">
        <rect x="0" y="0" width="40" height="70" rx="6" fill="rgba(15,23,42,0.9)" stroke="rgba(59,130,246,0.5)" strokeWidth="1.5">
          <animate attributeName="stroke-opacity" values="0.5;1;0.5" dur="2.2s" repeatCount="indefinite" begin="0.8s" />
        </rect>
        <rect x="5" y="8" width="30" height="50" rx="2" fill="rgba(30,58,138,0.4)" />
        <circle cx="20" cy="64" r="3" fill="rgba(59,130,246,0.5)" />
        <text x="20" y="78" textAnchor="middle" fill="rgba(96,165,250,0.7)" fontSize="6" fontFamily="Inter">MOBILE</text>
      </g>

      {/* Vendor logos as faint text */}
      {[
        { x: 80, y: 220, label: "Dell" },
        { x: 160, y: 230, label: "HP" },
        { x: 230, y: 220, label: "Cisco" },
        { x: 310, y: 230, label: "Microsoft" },
      ].map((v, i) => (
        <text key={i} x={v.x} y={v.y} textAnchor="middle" fill="rgba(59,130,246,0.25)" fontSize="10" fontFamily="Inter" fontWeight="700">
          <animate attributeName="opacity" values="0.15;0.35;0.15" dur={`${3 + i * 0.5}s`} repeatCount="indefinite" begin={`${i * 0.5}s`} />
          {v.label}
        </text>
      ))}

      {/* Float animation on devices */}
      <animateTransform attributeName="transform" type="translate" values="0,0;0,-4;0,0" dur="3s" repeatCount="indefinite" />

      <defs>
        <filter id="s8glow">
          <feGaussianBlur stdDeviation="4" result="blur" />
          <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
        </filter>
      </defs>
    </svg>
  );
}

// ─── Slide Data ───────────────────────────────────────────────────────────────
const SLIDES: Slide[] = [
  {
    id: 1,
    badge: "CORE SERVICE",
    badgeColor: "#3B82F6",
    serviceName: "MANAGED IT",
    headline: "Proactive IT Management for Growing Businesses",
    sub: "We monitor, manage, and support your complete technology environment — so your team stays focused on the business.",
    cta1: { label: "Get Managed IT Quote", href: "/contact" },
    cta2: { label: "Learn about Managed IT Services", href: "/managed-it-services" },
    trust: [{ text: "24/7 Monitoring" }, { text: "Fixed Monthly Pricing" }, { text: "Dedicated Support Team" }],
    bgBloom: "radial-gradient(ellipse 60% 60% at 70% 50%, rgba(37,99,235,0.18) 0%, transparent 70%)",
    Animation: ManagedITAnimation,
  },
  {
    id: 2,
    badge: "STRATEGIC CAPABILITY",
    badgeColor: "#06B6D4",
    serviceName: "INFRASTRUCTURE",
    headline: "End-to-End Infrastructure for Multi-Site Businesses",
    sub: "We design, deploy, and standardise complete technology environments across offices, retail locations, and distributed operations.",
    cta1: { label: "Start an Infrastructure Project", href: "/contact" },
    cta2: { label: "Learn about IT Infrastructure", href: "/it-infrastructure" },
    trust: [{ text: "Multi-Site Specialists" }, { text: "Full Project Management" }, { text: "Hardware to Cloud" }],
    bgBloom: "radial-gradient(ellipse 60% 60% at 70% 50%, rgba(6,182,212,0.15) 0%, transparent 70%)",
    Animation: InfrastructureAnimation,
  },
  {
    id: 3,
    badge: "NETWORK INFRASTRUCTURE",
    badgeColor: "#22D3EE",
    serviceName: "NETWORK SOLUTIONS",
    headline: "Reliable, Secure Networks Built for Business",
    sub: "From network architecture and hardware deployment to wireless infrastructure and multi-site connectivity — we keep your business connected.",
    cta1: { label: "Book a Network Review", href: "/contact" },
    cta2: { label: "Learn about Network Solutions", href: "/network-solutions" },
    trust: [{ text: "Enterprise-Grade Hardware" }, { text: "Secure by Design" }, { text: "Proactive Monitoring" }],
    bgBloom: "radial-gradient(ellipse 60% 60% at 70% 50%, rgba(34,211,238,0.12) 0%, transparent 70%)",
    Animation: NetworkAnimation,
  },
  {
    id: 4,
    badge: "SECURITY & PROTECTION",
    badgeColor: "#EF4444",
    serviceName: "CYBERSECURITY",
    headline: "Layered Cybersecurity for Modern Business Threats",
    sub: "Protecting your users, devices, networks, and data with a structured security framework built around the NIST model.",
    cta1: { label: "Book a Security Assessment", href: "/contact" },
    cta2: { label: "Learn about Cyber Security", href: "/cyber-security" },
    trust: [{ text: "Identify → Protect" }, { text: "Detect → Respond" }, { text: "Recover" }],
    bgBloom: "radial-gradient(ellipse 60% 60% at 70% 50%, rgba(239,68,68,0.1) 0%, transparent 70%)",
    Animation: CybersecurityAnimation,
  },
  {
    id: 5,
    badge: "CLOUD SOLUTIONS",
    badgeColor: "#0EA5E9",
    serviceName: "CLOUD SOLUTIONS",
    headline: "Enterprise Cloud Platforms for Modern Business",
    sub: "Pro IT Solutions delivers Microsoft Azure, Amazon AWS, and Microsoft 365 services — from cloud migration and infrastructure to modern workplace collaboration.",
    cta1: { label: "Book a Cloud Assessment", href: "/contact" },
    cta2: { label: "Learn about Cloud Solutions", href: "/cloud-solutions" },
    trust: [{ text: "Microsoft Azure" }, { text: "Amazon AWS" }, { text: "Microsoft 365" }],
    bgBloom: "radial-gradient(ellipse 60% 60% at 70% 50%, rgba(14,165,233,0.15) 0%, transparent 70%)",
    Animation: CloudAnimation,
  },
  {
    id: 6,
    badge: "UNIFIED COMMUNICATIONS",
    badgeColor: "#06B6D4",
    serviceName: "COMMUNICATIONS",
    headline: "Connect Your Teams with Modern Cloud Communications",
    sub: "Cloud phone systems, Microsoft Teams calling, and unified communications platforms that keep your business connected anywhere.",
    cta1: { label: "Book a Comms Review", href: "/contact" },
    cta2: { label: "Learn about Business Communications", href: "/business-communications" },
    trust: [{ text: "3CX & Teams Calling" }, { text: "Multi-Site Phone Systems" }, { text: "Replace Legacy Phone Lines" }],
    bgBloom: "radial-gradient(ellipse 60% 60% at 70% 50%, rgba(6,182,212,0.15) 0%, transparent 70%)",
    Animation: CommsAnimation,
  },
  {
    id: 7,
    badge: "AI & AUTOMATION",
    badgeColor: "#7C3AED",
    serviceName: "AI & AUTOMATION",
    headline: "Automate Workflows. Accelerate Business Operations.",
    sub: "Reduce manual effort, streamline processes, and leverage AI-powered tools to improve efficiency across your business.",
    cta1: { label: "Book an Automation Consultation", href: "/contact" },
    cta2: { label: "Learn about AI & Automation", href: "/ai-automation" },
    trust: [{ text: "Power Automate" }, { text: "AI-Assisted Tools" }, { text: "Workflow Integration" }],
    bgBloom: "radial-gradient(ellipse 60% 60% at 70% 50%, rgba(124,58,237,0.15) 0%, transparent 70%)",
    Animation: AIAnimation,
  },
  {
    id: 8,
    badge: "TECHNOLOGY SOURCING",
    badgeColor: "#60A5FA",
    serviceName: "ICT PROCUREMENT",
    headline: "The Right Technology, Sourced and Deployed for You",
    sub: "We help businesses source, deploy, and manage technology solutions — from laptops and servers to networking equipment and cloud platforms.",
    cta1: { label: "Request a Quote", href: "/contact" },
    cta2: { label: "Learn about ICT Procurement", href: "/ict-procurement" },
    trust: [{ text: "Competitive Pricing" }, { text: "Trusted Vendors" }, { text: "Lifecycle Management" }],
    bgBloom: "radial-gradient(ellipse 60% 60% at 70% 50%, rgba(96,165,250,0.12) 0%, transparent 70%)",
    Animation: ProcurementAnimation,
  },
];

const INTERVAL = 6000;

// ─── Main Component ───────────────────────────────────────────────────────────
export default function ServiceSlider() {
  const [current, setCurrent] = useState(0);
  const [direction, setDirection] = useState(1);
  const [progress, setProgress] = useState(0);
  const [paused, setPaused] = useState(false);
  const [hasInteracted, setHasInteracted] = useState(false);
  const shouldReduceMotion = useReducedMotion();
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const progressRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const startTimeRef = useRef<number>(Date.now());

  const goTo = useCallback((index: number, dir?: number) => {
    if (!hasInteracted) setHasInteracted(true);
    const d = dir !== undefined ? dir : index > current ? 1 : -1;
    setDirection(d);
    setCurrent(index);
    setProgress(0);
    startTimeRef.current = Date.now();
  }, [current, hasInteracted]);

  const goNext = useCallback(() => {
    goTo((current + 1) % SLIDES.length, 1);
  }, [current, goTo]);

  const goPrev = useCallback(() => {
    goTo((current - 1 + SLIDES.length) % SLIDES.length, -1);
  }, [current, goTo]);

  // Auto-advance
  useEffect(() => {
    if (paused) return;
    intervalRef.current = setInterval(() => {
      if (!hasInteracted) setHasInteracted(true);
      goNext();
    }, INTERVAL);
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [paused, goNext, hasInteracted]);

  // Progress bar
  useEffect(() => {
    setProgress(0);
    startTimeRef.current = Date.now();
    if (paused) return;
    progressRef.current = setInterval(() => {
      const elapsed = Date.now() - startTimeRef.current;
      setProgress(Math.min((elapsed / INTERVAL) * 100, 100));
    }, 50);
    return () => { if (progressRef.current) clearInterval(progressRef.current); };
  }, [current, paused]);

  // Keyboard navigation
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "ArrowRight") goNext();
      if (e.key === "ArrowLeft") goPrev();
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [goNext, goPrev]);

  const slide = SLIDES[current];
  const Animation = slide.Animation;

  const slideVariants = {
    enter: (dir: number) => ({
      opacity: 0,
      x: shouldReduceMotion ? 0 : dir * 60,
    }),
    center: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.7, ease: [0.4, 0, 0.2, 1] as const },
    },
    exit: (dir: number) => ({
      opacity: 0,
      x: shouldReduceMotion ? 0 : dir * -60,
      transition: { duration: 0.5, ease: [0.4, 0, 0.2, 1] as const },
    }),
  };

  return (
    <section
      className="relative h-screen flex flex-col overflow-hidden"
      style={{ background: "#070B14" }}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      aria-label="Service highlights slider"
    >
      {/* Background bloom per slide */}
      <AnimatePresence mode="wait">
        <motion.div
          key={`bloom-${current}`}
          className="absolute inset-0 pointer-events-none"
          style={{ background: slide.bgBloom, zIndex: 1 }}
          initial={hasInteracted ? { opacity: 0 } : false}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 1 }}
        />
      </AnimatePresence>

      {/* Aurora atmospheric glows */}
      <div className="absolute inset-0 pointer-events-none" style={{ zIndex: 1 }}>
        <div
          className="hero-aurora-1 absolute top-[5%] left-[5%] w-[600px] h-[500px] rounded-full"
          style={{
            background: "radial-gradient(ellipse, rgba(91,33,255,0.20) 0%, rgba(91,33,255,0.05) 50%, transparent 75%)",
            filter: "blur(100px)",
          }}
        />
        <div
          className="hero-aurora-2 absolute top-[15%] right-[0%] w-[550px] h-[450px] rounded-full"
          style={{
            background: "radial-gradient(ellipse, rgba(0,212,255,0.15) 0%, rgba(0,212,255,0.04) 50%, transparent 75%)",
            filter: "blur(100px)",
          }}
        />
        <div
          className="hero-aurora-3 absolute bottom-[5%] left-[30%] w-[500px] h-[350px] rounded-full"
          style={{
            background: "radial-gradient(ellipse, rgba(91,33,255,0.12) 0%, rgba(37,99,235,0.04) 50%, transparent 75%)",
            filter: "blur(90px)",
          }}
        />
      </div>

      {/* Grain texture overlay */}
      <div className="absolute inset-0 pointer-events-none grain-overlay" style={{ zIndex: 2 }} />

      {/* Grid overlay */}
      <div className="absolute inset-0 pointer-events-none grid-overlay" style={{ zIndex: 2 }} />

      {/* Left gradient overlay for readability */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: "linear-gradient(90deg, rgba(7,11,20,0.95) 0%, rgba(7,11,20,0.7) 45%, rgba(7,11,20,0.1) 70%, transparent 100%)",
          zIndex: 3,
        }}
      />

      {/* Slide counter — top right */}
      <div
        className="absolute top-20 right-4 sm:top-24 sm:right-8 z-20 font-heading font-600 text-sm tracking-widest"
        style={{ color: "rgba(148,163,184,0.6)" }}
      >
        <span style={{ color: "#60A5FA" }}>{String(current + 1).padStart(2, "0")}</span>
        <span className="mx-1">/</span>
        <span>{String(SLIDES.length).padStart(2, "0")}</span>
      </div>

      {/* Main slide content */}
      <div className="relative z-10 flex-1 flex items-center w-full pt-16">
        <div className="max-w-container mx-auto px-4 sm:px-6 w-full">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center w-full">

            {/* Left: Content */}
            <AnimatePresence mode="wait" custom={direction}>
              <motion.div
                key={`content-${current}`}
                custom={direction}
                variants={slideVariants}
                initial={hasInteracted ? "enter" : false}
                animate="center"
                exit="exit"
                className="flex flex-col justify-center py-6 sm:py-8"
              >
                {/* Badge */}
                <div
                  className="inline-flex items-center gap-2 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full mb-4 sm:mb-6 self-start"
                  style={{
                    border: `1px solid ${slide.badgeColor}40`,
                    background: `${slide.badgeColor}15`,
                  }}
                >
                  <span
                    className="w-2 h-2 rounded-full animate-pulse"
                    style={{ background: slide.badgeColor }}
                  />
                  <span
                    className="text-[11px] sm:text-[12px] font-heading font-600 tracking-widest uppercase"
                    style={{ color: slide.badgeColor }}
                  >
                    {slide.badge}
                  </span>
                </div>

                {/* Headline */}
                <h1
                  className="font-heading font-800 leading-[1.08] mb-4 sm:mb-5"
                  style={{
                    fontSize: "clamp(26px, 5vw, 58px)",
                    color: "#F8FAFC",
                    letterSpacing: "-0.03em",
                  }}
                >
                  {slide.headline}
                </h1>

                {/* Sub */}
                <p
                  className="text-[14px] sm:text-[16px] font-body leading-relaxed mb-6 sm:mb-8"
                  style={{ color: "rgba(203,213,225,0.75)" }}
                >
                  {slide.sub}
                </p>

                {/* CTAs */}
                <div className="flex flex-wrap gap-2 sm:gap-3 mb-6 sm:mb-8">
                  {/* Primary CTA */}
                  <motion.div
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.97 }}
                    transition={{ duration: 0.22, ease: "easeOut" }}
                    className="relative"
                  >
                    <span
                      className="absolute inset-0 rounded-full animate-ping"
                      style={{
                        background: "transparent",
                        border: "2px solid rgba(59,130,246,0.4)",
                        animationDuration: "2s",
                      }}
                    />
                    <motion.div
                      className="absolute inset-0 rounded-full pointer-events-none"
                      initial={{ opacity: 0 }}
                      whileHover={{ opacity: 1 }}
                      transition={{ duration: 0.22, ease: "easeOut" }}
                      style={{
                        boxShadow: "0 0 22px 6px rgba(59,130,246,0.55), 0 0 8px 2px rgba(96,165,250,0.4)",
                      }}
                    />
                    <Link
                      href={slide.cta1.href}
                      className="relative inline-flex items-center gap-2 px-6 py-3.5 text-[14px] font-heading font-600 btn-primary"
                    >
                      {slide.cta1.label}
                      <motion.svg
                        width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                        strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
                        whileHover={{ x: 3 }}
                        transition={{ duration: 0.22, ease: "easeOut" }}
                      >
                        <path d="M5 12h14M12 5l7 7-7 7" />
                      </motion.svg>
                    </Link>
                  </motion.div>

                  {/* Secondary CTA */}
                  <motion.div
                    whileHover={{ scale: 1.03 }}
                    whileTap={{ scale: 0.97 }}
                    transition={{ duration: 0.22, ease: "easeOut" }}
                    className="relative"
                  >
                    <motion.div
                      className="absolute inset-0 rounded-full pointer-events-none"
                      initial={{ opacity: 0 }}
                      whileHover={{ opacity: 1 }}
                      transition={{ duration: 0.22, ease: "easeOut" }}
                      style={{
                        boxShadow: "0 0 16px 4px rgba(34,211,238,0.35)",
                      }}
                    />
                    <Link
                      href={slide.cta2.href}
                      className="relative inline-flex items-center gap-2 px-6 py-3.5 text-[14px] font-heading font-500 btn-secondary"
                    >
                      {slide.cta2.label}
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M7 18L17 8M17 8L7 18M17 8V14M17 14L14 11" />
                      </svg>
                    </Link>
                  </motion.div>
                </div>

                {/* Trust points */}
                <div className="flex flex-wrap gap-x-4 sm:gap-x-6 gap-y-2">
                  {slide.trust.map((t, i) => (
                    <span
                      key={i}
                      className="flex items-center gap-2 text-[12px] sm:text-[13px] font-body"
                      style={{ color: "rgba(148,163,184,0.8)" }}
                    >
                      <span
                        className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                        style={{ background: slide.badgeColor }}
                      />
                      {t.text}
                    </span>
                  ))}
                </div>
              </motion.div>
            </AnimatePresence>

            {/* Right: Animation */}
            <AnimatePresence mode="wait" custom={direction}>
              <motion.div
                key={`anim-${current}`}
                custom={direction}
                variants={{
                  enter: (dir: number) => ({ opacity: 0, x: shouldReduceMotion ? 0 : dir * 80 }),
                  center: { opacity: 1, x: 0, transition: { duration: 0.7, delay: 0.1, ease: [0.4, 0, 0.2, 1] } },
                  exit: (dir: number) => ({ opacity: 0, x: shouldReduceMotion ? 0 : dir * -80, transition: { duration: 0.5, ease: [0.4, 0, 0.2, 1] } }),
                }}
                initial={hasInteracted ? "enter" : false}
                animate="center"
                exit="exit"
                className="hidden lg:flex items-center justify-center"
              >
                <div className="w-full max-w-[560px] aspect-[4/3]">
                  <Animation />
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>

      {/* Service name label — lower left overlay */}
      <AnimatePresence mode="wait">
        <motion.div
          key={`servicelabel-${current}`}
          className="absolute bottom-20 sm:bottom-24 left-4 sm:left-8 z-20 flex items-center gap-3"
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          transition={{ duration: 0.4, ease: [0.4, 0, 0.2, 1] }}
        >
          <span
            className="block h-[2px] w-6 rounded-full flex-shrink-0"
            style={{ background: "linear-gradient(90deg, #2563EB, #22D3EE)" }}
          />
          <span
            className="text-[11px] font-heading tracking-[0.2em] uppercase"
            style={{ color: "rgba(148,163,184,0.75)", letterSpacing: "0.18em" }}
          >
            {slide.serviceName}
          </span>
        </motion.div>
      </AnimatePresence>

      {/* Left arrow */}
      <button
        onClick={goPrev}
        className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 z-30 w-8 h-8 sm:w-9 sm:h-9 rounded-full flex items-center justify-center transition-all duration-200 hover:scale-110"
        style={{
          background: "rgba(15,23,42,0.7)",
          border: "1px solid rgba(59,130,246,0.3)",
          backdropFilter: "blur(8px)",
        }}
        aria-label="Previous slide"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="rgba(148,163,184,0.8)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M15 18l-6-6 6-6" />
        </svg>
      </button>

      {/* Right arrow */}
      <button
        onClick={goNext}
        className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 z-30 w-8 h-8 sm:w-9 sm:h-9 rounded-full flex items-center justify-center transition-all duration-200 hover:scale-110"
        style={{
          background: "rgba(15,23,42,0.7)",
          border: "1px solid rgba(59,130,246,0.3)",
          backdropFilter: "blur(8px)",
        }}
        aria-label="Next slide"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="rgba(148,163,184,0.8)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M9 18l6-6-6-6" />
        </svg>
      </button>

      {/* Scroll-down chevron indicator */}
      <div className="absolute bottom-20 sm:bottom-24 left-1/2 -translate-x-1/2 z-20 flex flex-col items-center gap-1 pointer-events-none">
        <span className="text-[10px] font-heading font-500 uppercase tracking-widest" style={{ color: "rgba(148,163,184,0.4)" }}>Scroll</span>
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="rgba(148,163,184,0.5)"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="animate-bounce-slow"
        >
          <path d="M6 9l6 6 6-6" />
        </svg>
      </div>

      {/* Bottom controls: dots + progress bar */}
      <div className="absolute bottom-0 left-0 right-0 z-20 pb-6 sm:pb-8 px-4 sm:px-6">
        {/* Progress bar */}
        <div
          className="w-full h-[2px] mb-6 rounded-full overflow-hidden"
          style={{ background: "rgba(59,130,246,0.15)" }}
        >
          <motion.div
            className="h-full rounded-full"
            style={{ background: "linear-gradient(90deg, #2563EB, #3B82F6, #60A5FA)", width: `${progress}%` }}
            transition={{ duration: 0.05 }}
          />
        </div>

        {/* Dot navigation */}
        <div className="flex items-center justify-center gap-2">
          {SLIDES.map((s, i) => (
            <button
              key={s.id}
              onClick={() => goTo(i)}
              className="transition-all duration-300 rounded-full"
              style={{
                width: i === current ? "28px" : "8px",
                height: "8px",
                background: i === current ? "#3B82F6" : "rgba(59,130,246,0.3)",
              }}
              aria-label={`Go to slide ${i + 1}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
